import React, { useState, useRef } from 'react';
import axios from 'axios';
import endpoints from '../../define/endpoints';
import ModalDrawer from './ModalDrawer';
import { useForm } from 'react-hook-form';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { Timeline } from '../Timeline/Timeline';
import { TRANSLATIONS } from '../../../translations';
import { RadioList } from './RadioList';
import { BetaTextInput } from '../BetaTextInput';
import { Loader } from '../common';
import Typography, {
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';

const SUCCESS_ICON = 'circleCheck';
const FAIL_ICON = 'warning';
const CANCELLATION_REASON = 'cancellation_reason';
const CUSTOM_REASON = 'custom_reason';
const REASON = 'reason';

const ModalCancelGiftcardForm = ({
  isOpen,
  setIsOpen,
  token,
  orderId,
  isDeviceSmallerThanMblLandscape,
  setRequestDetails,
}) => {
  const [showResponse, setShowResponse] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [responseContent, setResponseContent] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [radioListError, setRadioListError] = useState(null);
  const [motivationError, setMotivationError] = useState(null);

  const { register, handleSubmit, setValue, watch } = useForm();

  const formRef = useRef(null);
  const reasonslist = {
    cancellation_reason_1: TRANSLATIONS.ERROR_IN_GIFTCARD_PURCHASE,
    cancellation_reason_2: TRANSLATIONS.A_MORE_ADVANTAGEOUS_OFFER_FOUND,
    cancellation_reason_3:
      TRANSLATIONS.GIFT_NO_LONGER_SUITABLE_FOR_THE_RECIPIENT,
    other: TRANSLATIONS.OTHER,
  };

  const onSubmit = async (data, event) => {
    event.preventDefault();
    const reason = data.reason || '';

    if ((!reason || reason === '') && !showCustomInput) {
      setRadioListError(TRANSLATIONS.RADIO_LIST_ERROR);
      return;
    } else if ((!reason || reason === '') && showCustomInput) {
      setMotivationError(TRANSLATIONS.MOTIVATION_ERROR);
      return;
    }
    setIsLoading(true);
    setIsOpen(false);
    setShowResponse(true);
    try {
      const { data } = await axios.post(
        `${endpoints.core}/order-customer-request/store`,
        {
          order_id: orderId,
          type: 'giftcard_delete',
          reason: reason,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let requestDetails = data && data.order ? data.order : null;
      setRequestDetails(requestDetails);

      setIsSuccess(true);
      setResponseContent(
        TRANSLATIONS.CANCEL_GIFTCARD_REQUEST_HAS_BEEN_SUCCESSFULLY_SUBMITTED
      );
    } catch (error) {
      setResponseContent(
        TRANSLATIONS.UNEXPECTED_ERROR_HAS_OCCURRED_PLEASE_RETRY_LATER
      );
    } finally {
      setIsLoading(false);
    }
  };

  const responseModalDrawer = (
    <ModalDrawer
      isOpen={showResponse}
      setIsOpen={setShowResponse}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={600}
      title={TRANSLATIONS.CANCEL_GIFTCARD_REQUEST}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100%">
          {isLoading ? (
            <div className="d-flex justify-center text-center">
              <Loader loading={isLoading} />
            </div>
          ) : (
            <>
              <div className="d-flex flex-column items-center pb-2xs gap-xs">
                <BetaIcon
                  name={isSuccess ? SUCCESS_ICON : FAIL_ICON}
                  color={IconColors.regular}
                  size={IconSizes[64]}
                />
              </div>
              <div className="d-flex text-center align-center">
                <div
                  className="typography__p2 typography__neutral text-center"
                  dangerouslySetInnerHTML={{ __html: responseContent }}
                />
              </div>
            </>
          )}
        </div>
      }
      footer={
        <div className="d-flex justify-center gap-m pb-m pl-xl pr-xl">
          <Button
            classNames={'pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={<b>{TRANSLATIONS.CLOSE}</b>}
            onClick={() => setShowResponse(false)}
          />
        </div>
      }
    />
  );

  const form = (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <div className="pb-2xs">
        <RadioList
          inputName={CANCELLATION_REASON}
          options={reasonslist}
          defaultValue={null}
          onChange={(value) => {
            setValue(CANCELLATION_REASON, value);
            if (value === 'other') {
              setShowCustomInput(true);
              setValue(REASON, '');
            } else {
              setShowCustomInput(false);
              setValue(REASON, reasonslist[value]);
            }
          }}
          oneColumn={true}
          error={radioListError}
          setError={setRadioListError}
        />
      </div>

      {showCustomInput && (
        <>
          <BetaTextInput
            asTextArea
            className="modal-ord-li-review__review-form__field"
            label={TRANSLATIONS.INSERT_MESSAGE}
            name={CUSTOM_REASON}
            setRef={register({ required: false, maxLength: { value: 500 } })}
            defaultValue={''}
            onChange={(event) => {
              // const value = event.target.value.replace(
              //   /[^a-zA-Z0-9\s.,!?'-]/g,
              //   ''
              // );
              const value = event.target.value;
              setValue(REASON, value);
              setValue(CUSTOM_REASON, value);
            }}
            resize={true}
            limit={500}
            error={motivationError}
            setError={setMotivationError}
            errorMessage={TRANSLATIONS.MOTIVATION_ERROR}
          />
        </>
      )}
      <input type="hidden" name="reason" ref={register} />
    </form>
  );

  const resetErrors = () => {
    setTimeout(() => {
      setRadioListError(null);
      setMotivationError(null);
    }, 500);
  };

  return (
    <>
      {responseModalDrawer}
      <ModalDrawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
        modalCustomWidth={600}
        title={TRANSLATIONS.CANCEL_GIFTCARD_REQUEST}
        content={
          <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-xl">
            <div>
              <Typography
                classNames="text-left"
                variant={TypographyVariants.p2}
                weight={TypographyWeights.bold}
              >
                {TRANSLATIONS.WANT_TO_TELL_US_WHY_YOU_WANT_TO_CANCEL}
              </Typography>
              <Typography
                classNames="text-left"
                variant={TypographyVariants.p3}
              >
                {TRANSLATIONS.THESE_INFORMATIONS_HELP_US_IMPROVE_OUR_SERVICE}
              </Typography>
            </div>
            {form}
          </div>
        }
        footer={
          <div className="d-flex gap-m pb-m pl-xl pr-xl">
            <Button
              classNames={'flex-1 pr-m pl-m'}
              size={ButtonSizes.s}
              style={ButtonStyles.empty}
              label={TRANSLATIONS.CANCEL}
              onClick={() => {
                setIsOpen(false);
                resetErrors();
              }}
            />
            <Button
              classNames={'flex-1 pr-m pl-m'}
              size={ButtonSizes.s}
              style={ButtonStyles.secondary}
              label={TRANSLATIONS.CONFIRM}
              onClick={() => {
                formRef.current.dispatchEvent(
                  new Event('submit', { cancelable: true })
                );
              }}
            />
          </div>
        }
        handleAfterClose={resetErrors}
      />
    </>
  );
};

ModalCancelGiftcardForm.propTypes = {};

export { ModalCancelGiftcardForm };
